// frontend/src/hooks/useGeolocation.js

import { useState, useCallback } from "react";

const SESSION_KEY = "userGeolocation";
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

const getCachedLocation = () => {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const { data, timestamp } = JSON.parse(raw);
    if (Date.now() - timestamp < CACHE_TTL) return data;
    sessionStorage.removeItem(SESSION_KEY);
  } catch {}
  return null;
};

const setCachedLocation = (data) => {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({ data, timestamp: Date.now() }));
  } catch {}
};

export const useGeolocation = () => {
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const requestGeolocation = useCallback(async () => {
    const cached = getCachedLocation();
    if (cached) {
      setLocation(cached);
      return cached;
    }

    if (!navigator.geolocation) {
      setError("Geolocation not supported");
      return null;
    }

    setLoading(true);
    setError(null);

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          try {
            const { latitude, longitude, accuracy } = position.coords;
            const locationData = { lat: latitude, lng: longitude, accuracy };
            setCachedLocation(locationData);
            setLocation(locationData);
            setLoading(false);
            resolve(locationData);
          } catch (err) {
            setError(err.message);
            setLoading(false);
            resolve(null);
          }
        },
        (err) => {
          setError(err.message);
          setLoading(false);
          resolve(null);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        },
      );
    });
  }, []);

  return {
    location,
    loading,
    error,
    requestGeolocation,
  };
};
